import { StatCard } from "./StatCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Users, UserPlus, DollarSign, ClipboardCheck,
  AlertTriangle, HeadphonesIcon, Shield, TrendingUp, BookOpen,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  ChartContainer, ChartTooltip, ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, PieChart, Pie, Cell, CartesianGrid } from "recharts";

const COLORS = ["hsl(var(--primary))", "hsl(var(--accent-orange))", "hsl(var(--accent-blue))", "hsl(var(--destructive))", "hsl(var(--muted-foreground))", "#6366f1", "#10b981", "#f59e0b", "#ef4444"];

const STATUS_COLORS: Record<string, string> = {
  registered_active: "#10b981",
  unregistered_active: "#f59e0b",
  unregistered_inactive: "#94a3b8",
  active: "hsl(var(--primary))",
  suspended: "#ef4444",
  discontinued: "#dc2626",
  terminated: "#991b1b",
  postponed: "#8b5cf6",
  graduated: "#6366f1",
  deceased: "#374151",
  registered_inactive: "#f97316",
};

const fmtStatus = (s: string) => s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

export function PrincipalDashboard() {
  const { data: studentCount = 0 } = useQuery({
    queryKey: ["principal-students"],
    queryFn: async () => {
      const { count } = await supabase.from("students").select("*", { count: "exact", head: true });
      return count ?? 0;
    },
  });

  const { data: studentStatusCounts = [] } = useQuery({
    queryKey: ["principal-student-statuses"],
    queryFn: async () => {
      const { data } = await supabase.from("students").select("status");
      if (!data) return [];
      const counts: Record<string, number> = {};
      data.forEach(s => { counts[s.status] = (counts[s.status] || 0) + 1; });
      return Object.entries(counts).map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count);
    },
  });

  const { data: appCounts } = useQuery({
    queryKey: ["principal-apps"],
    queryFn: async () => {
      const { data } = await supabase.from("applications").select("status");
      return {
        total: data?.length ?? 0,
        pending: data?.filter(a => a.status === "pending").length ?? 0,
        approved: data?.filter(a => a.status === "approved").length ?? 0,
        rejected: data?.filter(a => a.status === "rejected").length ?? 0,
      };
    },
  });

  const { data: examStats } = useQuery({
    queryKey: ["principal-exams"],
    queryFn: async () => {
      const { data } = await supabase.from("exam_results").select("status, marks");
      const avgMarks = data?.length ? Math.round(data.reduce((s, r) => s + (Number(r.marks) || 0), 0) / data.length) : 0;
      return {
        pending: data?.filter(r => r.status === "pending").length ?? 0,
        published: data?.filter(r => r.status === "published").length ?? 0,
        avgMarks,
      };
    },
  });

  const { data: invoiceStats } = useQuery({
    queryKey: ["principal-invoices"],
    queryFn: async () => {
      const { data } = await supabase.from("invoices").select("amount, status, due_date");
      const unpaid = data?.filter(i => i.status === "unpaid") ?? [];
      const outstanding = unpaid.reduce((s, i) => s + Number(i.amount), 0);
      const overdue = unpaid.filter(i => i.due_date && new Date(i.due_date) < new Date()).length;
      return { outstanding, overdue };
    },
  });

  const { data: paymentTotal = 0 } = useQuery({
    queryKey: ["principal-payments"],
    queryFn: async () => {
      const { data } = await supabase.from("payments").select("amount");
      return data?.reduce((s, p) => s + Number(p.amount), 0) ?? 0;
    },
  });

  const { data: disciplineStats } = useQuery({
    queryKey: ["principal-discipline"],
    queryFn: async () => {
      const { data } = await supabase.from("discipline_cases").select("status");
      return {
        open: data?.filter(c => c.status === "open" || c.status === "investigation").length ?? 0,
        total: data?.length ?? 0,
      };
    },
  });

  const { data: serviceStats } = useQuery({
    queryKey: ["principal-services"],
    queryFn: async () => {
      const { data } = await supabase.from("service_requests").select("status");
      return {
        open: data?.filter(r => r.status === "open").length ?? 0,
        escalated: data?.filter(r => r.status === "escalated").length ?? 0,
        total: data?.length ?? 0,
      };
    },
  });

  const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`;

  const appChartData = [
    { name: "Pending", value: appCounts?.pending ?? 0 },
    { name: "Approved", value: appCounts?.approved ?? 0 },
    { name: "Rejected", value: appCounts?.rejected ?? 0 },
  ];

  const trendsData = [
    { name: "Students", value: studentCount },
    { name: "Applications", value: appCounts?.total ?? 0 },
    { name: "Discipline", value: disciplineStats?.total ?? 0 },
    { name: "Service Req.", value: serviceStats?.total ?? 0 },
  ];

  return (
    <>
      <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total Students" value={studentCount.toLocaleString()} icon={Users} description="Active enrollment" trend="up" />
        <StatCard title="Applications" value={`${appCounts?.pending ?? 0} pending`} icon={UserPlus} description={`${appCounts?.total ?? 0} total`} />
        <StatCard title="Avg. Marks" value={`${examStats?.avgMarks ?? 0}%`} icon={BookOpen} description={`${examStats?.pending ?? 0} pending approval`} trend="up" />
        <StatCard title="Fee Collection" value={fmt(paymentTotal)} icon={DollarSign} description={`${fmt(invoiceStats?.outstanding ?? 0)} outstanding`} trend="up" />
      </div>

      <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Overdue Invoices" value={invoiceStats?.overdue ?? 0} icon={AlertTriangle} description="Need follow-up" trend={invoiceStats?.overdue ? "down" : "neutral"} />
        <StatCard title="Pending Approvals" value={(examStats?.pending ?? 0) + (appCounts?.pending ?? 0)} icon={ClipboardCheck} description="Results & applications" />
        <StatCard title="Discipline Cases" value={disciplineStats?.open ?? 0} icon={Shield} description={`${disciplineStats?.total ?? 0} total cases`} />
        <StatCard title="Service Requests" value={`${serviceStats?.open ?? 0} open`} icon={HeadphonesIcon} description={`${serviceStats?.escalated ?? 0} escalated`} />
      </div>

      <div className="grid gap-2 sm:gap-3 grid-cols-1 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-lg">Institution Overview</CardTitle></CardHeader>
          <CardContent>
            <ChartContainer config={{ value: { label: "Count", color: "hsl(var(--primary))" } }} className="h-[250px]">
              <BarChart data={trendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Admissions Breakdown</CardTitle></CardHeader>
          <CardContent>
            <ChartContainer config={{ pending: { label: "Pending", color: COLORS[1] }, approved: { label: "Approved", color: COLORS[0] }, rejected: { label: "Rejected", color: COLORS[3] } }} className="h-[250px]">
              <PieChart>
                <Pie data={appChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" nameKey="name" label>
                  {appChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <ChartTooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Students by Status</CardTitle>
          <CardDescription>{studentCount} total students</CardDescription>
        </CardHeader>
        <CardContent>
          {studentStatusCounts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No student data</p>
          ) : (
            <ChartContainer config={Object.fromEntries(studentStatusCounts.map(s => [s.status, { label: fmtStatus(s.status), color: STATUS_COLORS[s.status] || "#94a3b8" }]))} className="h-[280px]">
              <BarChart data={studentStatusCounts} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis type="category" dataKey="status" tickFormatter={fmtStatus} width={140} tick={{ fontSize: 12 }} />
                <ChartTooltip content={<ChartTooltipContent nameKey="status" labelFormatter={fmtStatus} />} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {studentStatusCounts.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || "#94a3b8"} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>
    </>
  );
}
